import { useState } from "react";
import { useEmployeeHours, useHrInvalidate } from "@/hooks/use-hr-queries";
import { hrEmployeeApi } from "@/lib/api/hr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Page, Panel, QueryGate, Stat } from "@/components/shell/page";
import { todayIST } from "@/lib/timezone";

export default function EmployeeHoursPage() {
  const { data, isLoading, error } = useEmployeeHours();
  const invalidate = useHrInvalidate();
  const [workDate, setWorkDate] = useState(todayIST());
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("18:00");
  const [breakMinutes, setBreakMinutes] = useState("60");
  const [description, setDescription] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await hrEmployeeApi.logHours({
      work_date: workDate,
      start_time: start,
      end_time: end,
      break_minutes: Number(breakMinutes),
      description,
    });
    invalidate();
  }

  return (
    <QueryGate loading={isLoading} error={error || !data ? "Could not load hours." : null}>
      {data && (
        <Page title="Hours" description="Log the day. Totals are added up from each entry.">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Today" value={data.today} hint="hours" />
            <Stat label="This week" value={data.week} hint="hours" />
            <Stat label="This month" value={data.month} hint="hours" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Log a day">
              <form onSubmit={submit} className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="work-date">Date</Label>
                    <Input id="work-date" type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="break">Break (minutes)</Label>
                    <Input id="break" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="start">Start</Label>
                    <Input id="start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="end">End</Label>
                    <Input id="end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="work-note">What you worked on</Label>
                  <Textarea id="work-note" value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                <Button type="submit">Save hours</Button>
              </form>
            </Panel>
            <Panel title="This month">
              {(data.logs ?? []).length === 0 && (
                <EmptyState title="No hours logged" body="Saved days will list here." />
              )}
              <ul className="divide-y">
                {(data.logs ?? []).map((log: any) => (
                  <li key={log.id} className="flex items-center justify-between py-3 text-sm first:pt-0 last:pb-0">
                    <span>{log.work_date}</span>
                    <span className="font-medium tabular-nums">{log.hours}h</span>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        </Page>
      )}
    </QueryGate>
  );
}
